/**
 * "New since you last opened this job" (#1717) — the dot in the calm job
 * page's Conversation.
 *
 * No server change: it is a per-user preference (`user_preferences`, the same
 * `/api/me/preferences/:key` store `useUserPreference` writes), one small key
 * per job the person actually opens: `job-page.seen:<jobId>` (50 characters,
 * inside the 60 the route allows).
 *
 * On open it reads the stored time ONCE and keeps it for the visit (so the
 * dots don't vanish while you read), then writes "now" for next time. A first
 * visit has no stored time, so nothing is marked new.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export const lastViewedKey = (jobId: string) => `job-page.seen:${jobId}`;

export function useJobLastViewed(
    jobId: string | undefined,
    enabled = true,
): { lastViewed: string | null; ready: boolean } {
    const key = jobId ? lastViewedKey(jobId) : "";
    const q = useQuery({
        queryKey: ["user-preference", key],
        queryFn: () => api.get<{ value: string | null }>(`/api/me/preferences/${encodeURIComponent(key)}`),
        enabled: !!jobId && enabled,
        staleTime: Infinity,
        retry: false,
    });
    const [snapshot, setSnapshot] = useState<{ jobId: string; value: string | null } | null>(null);
    const wrote = useRef<string | null>(null);

    useEffect(() => {
        if (!jobId || !enabled) return;
        if (!q.isSuccess && !q.isError) return;
        if (snapshot?.jobId === jobId) return;
        const prev = q.isSuccess && typeof q.data?.value === "string" ? q.data.value : null;
        setSnapshot({ jobId, value: prev });
        if (wrote.current === jobId) return;
        wrote.current = jobId;
        // Best effort: a failed write only means the dots repeat next time.
        void api
            .put(`/api/me/preferences/${encodeURIComponent(lastViewedKey(jobId))}`, { value: new Date().toISOString() })
            .catch(() => undefined);
    }, [jobId, enabled, q.isSuccess, q.isError, q.data, snapshot]);

    const ready = snapshot?.jobId === jobId;
    return { lastViewed: ready ? snapshot!.value : null, ready };
}
